import { useState, useMemo } from "react";
import { readSettings } from "@/hooks/use-settings";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmt, ANGLE_PRESETS, ResultCard } from "./pipe-helpers";
import {
  PIPE_MATERIALS_HW,
  EQ_LENGTH_DATA, FITTING_TYPES, EQ_PIPE_SIZES, EQ_MATERIALS,
  GPM_SIZING_MATERIALS, VELOCITY_PRESETS,
  MAX_GPM_VELOCITIES, MAX_GPM_MATERIALS,
} from "./pipe-reference-data";

/* ═══════════════════════════════════════════════════════
   PIPE OFFSET CALCULATOR
   ═══════════════════════════════════════════════════════ */
export const OffsetCalculator = () => {
  const defaults = readSettings();
  const [offset, setOffset] = useState("");
  const [angle, setAngle] = useState<string>(defaults.fittingAngle);

  const deg = parseFloat(angle);
  const off = parseFloat(offset);
  const rad = (deg * Math.PI) / 180;
  const travel = off / Math.sin(rad);
  const run = off / Math.tan(rad);

  return (
    <div className="px-4 space-y-4">
      <div className="space-y-3">
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">
            Offset (Set)
          </label>
          <Input type="number" inputMode="decimal" placeholder="Enter offset distance…" value={offset} onChange={(e) => setOffset(e.target.value)} className="font-mono-data bg-card border-border" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">
            Fitting Angle
          </label>
          <Select value={angle} onValueChange={setAngle}>
            <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ANGLE_PRESETS.map((a) => (
                <SelectItem key={a.deg} value={String(a.deg)}>{a.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {off > 0 && deg > 0 && deg < 180 && (
        <div className="space-y-2 animate-fade-in">
          <ResultCard label="Travel" value={fmt(travel)} unit="in" accent />
          <ResultCard label="Run" value={fmt(run)} unit="in" />
          <ResultCard label="Set (Offset)" value={fmt(off)} unit="in" />
          <ResultCard label="Angle" value={`${angle}°`} unit="" />
        </div>
      )}

      <div className="mt-4">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Quick-Ref Multipliers</h3>
        <div className="grid grid-cols-3 gap-2 text-center text-xs">
          <div className="bg-card rounded-lg p-2 border border-border">
            <div className="text-muted-foreground">Angle</div>
            <div className="font-semibold text-foreground mt-0.5">Travel ×</div>
          </div>
          {ANGLE_PRESETS.filter(a => a.deg < 90).map(a => {
            const r = (a.deg * Math.PI) / 180;
            return (
              <div key={a.deg} className="bg-card rounded-lg p-2 border border-border">
                <div className="text-muted-foreground">{a.label}</div>
                <div className="font-mono-data font-bold text-primary mt-0.5">{(1 / Math.sin(r)).toFixed(4)}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   ROLLING OFFSET CALCULATOR (3D)
   ═══════════════════════════════════════════════════════ */
export const RollingOffsetCalculator = () => {
  const defaults = readSettings();
  const [rise, setRise] = useState("");
  const [roll, setRoll] = useState("");
  const [angle, setAngle] = useState<string>(defaults.fittingAngle);

  const riseN = parseFloat(rise);
  const rollN = parseFloat(roll);
  const deg = parseFloat(angle);
  const rad = (deg * Math.PI) / 180;

  const hasInput = riseN > 0 && rollN > 0 && deg > 0 && deg < 180;
  const rollingOffset = Math.sqrt(riseN * riseN + rollN * rollN);
  const travel = rollingOffset / Math.sin(rad);
  const run = rollingOffset / Math.tan(rad);
  const rollAngle = (Math.atan2(rollN, riseN) * 180) / Math.PI;

  return (
    <div className="px-4 space-y-4">
      <div className="bg-card border border-border rounded-xl p-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          A <span className="text-foreground font-semibold">rolling offset</span> combines a vertical rise and a horizontal roll into a single diagonal measurement. Enter both components and the fitting angle to calculate the true travel, run, and offset.
        </p>
      </div>

      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Rise (vertical)</label>
            <Input type="number" inputMode="decimal" placeholder="Vertical…" value={rise} onChange={(e) => setRise(e.target.value)} className="font-mono-data bg-card border-border" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Roll (horizontal)</label>
            <Input type="number" inputMode="decimal" placeholder="Horizontal…" value={roll} onChange={(e) => setRoll(e.target.value)} className="font-mono-data bg-card border-border" />
          </div>
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Fitting Angle</label>
          <Select value={angle} onValueChange={setAngle}>
            <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ANGLE_PRESETS.map((a) => (
                <SelectItem key={a.deg} value={String(a.deg)}>{a.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {hasInput && (
        <div className="space-y-2 animate-fade-in">
          <div className="bg-card border border-border rounded-xl p-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">3D Geometry</h3>
            <svg viewBox="0 0 320 240" className="w-full h-auto" style={{ maxHeight: 220 }}>
              {[0, 1, 2, 3, 4].map(i => (
                <line key={`g${i}`} x1={40 + i * 20} y1={200 - i * 10} x2={280 + i * 5} y2={200 - i * 10} className="stroke-border" strokeWidth="0.5" strokeDasharray="4 4" />
              ))}
              {(() => {
                const bx = 60, by = 190;
                const maxDim = Math.max(riseN, rollN, 1);
                const riseH = (riseN / maxDim) * 120;
                const rollW = (rollN / maxDim) * 160;
                const topX = bx, topY = by - riseH;
                const cornerX = bx + rollW, cornerY = by;
                const endX = bx + rollW, endY = by - riseH;

                return (
                  <g>
                    <path d={`M ${bx} ${by} L ${cornerX} ${cornerY} L ${cornerX + 30} ${cornerY - 15} L ${bx + 30} ${by - 15} Z`} className="fill-muted/30" />
                    <line x1={bx} y1={by} x2={topX} y2={topY} className="stroke-primary" strokeWidth="2.5" strokeLinecap="round" />
                    <text x={bx - 8} y={(by + topY) / 2} textAnchor="end" className="fill-primary text-[10px] font-semibold" fontFamily="monospace">Rise</text>
                    <text x={bx - 8} y={(by + topY) / 2 + 12} textAnchor="end" className="fill-primary text-[9px]" fontFamily="monospace">{fmt(riseN)}"</text>
                    <line x1={bx} y1={by} x2={cornerX} y2={cornerY} className="stroke-foreground" strokeWidth="2" strokeLinecap="round" strokeDasharray="6 3" />
                    <text x={(bx + cornerX) / 2} y={by + 16} textAnchor="middle" className="fill-foreground text-[10px] font-semibold" fontFamily="monospace">Roll {fmt(rollN)}"</text>
                    <line x1={bx} y1={by} x2={endX} y2={endY} strokeWidth="2.5" strokeLinecap="round" stroke="hsl(var(--copper))" />
                    <text x={(bx + endX) / 2 + 5} y={(by + endY) / 2 - 6} textAnchor="start" style={{ fill: "hsl(var(--copper))" }} className="text-[10px] font-semibold" fontFamily="monospace">Offset {fmt(rollingOffset)}"</text>
                    <line x1={bx} y1={by} x2={endX + (run / maxDim) * 30} y2={endY - 10} strokeWidth="3" strokeLinecap="round" opacity="0.4" stroke="hsl(var(--pipe-green))" strokeDasharray="8 4" />
                    <line x1={endX} y1={endY} x2={cornerX} y2={cornerY} className="stroke-muted-foreground" strokeWidth="1" strokeDasharray="3 3" />
                    {rollAngle > 0 && rollAngle < 90 && (
                      <path d={`M ${bx} ${by - 25} A 25 25 0 0 1 ${bx + 25 * Math.sin(rollAngle * Math.PI / 180)} ${by - 25 * Math.cos(rollAngle * Math.PI / 180)}`} fill="none" className="stroke-muted-foreground" strokeWidth="1" />
                    )}
                    <text x={bx + 18} y={by - 28} className="fill-muted-foreground text-[8px]" fontFamily="monospace">{fmt(rollAngle, 1)}°</text>
                    <circle cx={bx} cy={by} r="4" className="fill-primary" />
                    <circle cx={endX} cy={endY} r="4" stroke="hsl(var(--copper))" fill="none" strokeWidth="2" />
                    <circle cx={cornerX} cy={cornerY} r="2.5" className="fill-muted-foreground" />
                    <text x={bx} y={by + 16} textAnchor="start" className="fill-muted-foreground text-[8px]" fontFamily="monospace">START</text>
                    <text x={endX + 4} y={endY - 6} textAnchor="start" className="fill-muted-foreground text-[8px]" fontFamily="monospace">END</text>
                    <g transform="translate(200, 20)">
                      <line x1="0" y1="0" x2="18" y2="0" className="stroke-primary" strokeWidth="2" />
                      <text x="22" y="4" className="fill-muted-foreground text-[8px]" fontFamily="monospace">Rise</text>
                      <line x1="0" y1="14" x2="18" y2="14" stroke="hsl(var(--copper))" strokeWidth="2" />
                      <text x="22" y="18" className="fill-muted-foreground text-[8px]" fontFamily="monospace">Offset</text>
                      <line x1="0" y1="28" x2="18" y2="28" stroke="hsl(var(--pipe-green))" strokeWidth="2" strokeDasharray="4 2" />
                      <text x="22" y="32" className="fill-muted-foreground text-[8px]" fontFamily="monospace">Travel</text>
                    </g>
                  </g>
                );
              })()}
            </svg>
          </div>

          <ResultCard label="Rolling Offset" value={fmt(rollingOffset)} unit="in" accent />
          <ResultCard label="Travel" value={fmt(travel)} unit="in" accent />
          <ResultCard label="Run" value={fmt(run)} unit="in" />
          <div className="grid grid-cols-2 gap-2">
            <ResultCard label="Rise" value={fmt(riseN)} unit="in" />
            <ResultCard label="Roll" value={fmt(rollN)} unit="in" />
          </div>
          <ResultCard label="Roll Angle" value={fmt(rollAngle, 1) + "°"} unit="" />
          <ResultCard label="Fitting Angle" value={`${angle}°`} unit="" />
        </div>
      )}

      {hasInput && (
        <div className="bg-card border border-border rounded-xl p-4 mt-2 animate-fade-in">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Breakdown</h3>
          <div className="space-y-1.5 text-xs font-mono-data text-muted-foreground">
            <p>Rolling Offset = √(Rise² + Roll²)</p>
            <p className="text-foreground">= √({riseN}² + {rollN}²) = <span className="text-primary font-bold">{fmt(rollingOffset)}"</span></p>
            <p className="mt-2">Travel = Offset ÷ sin({angle}°)</p>
            <p className="text-foreground">= {fmt(rollingOffset)} ÷ {fmt(Math.sin(rad), 4)} = <span className="text-primary font-bold">{fmt(travel)}"</span></p>
            <p className="mt-2">Run = Offset ÷ tan({angle}°)</p>
            <p className="text-foreground">= {fmt(rollingOffset)} ÷ {fmt(Math.tan(rad), 4)} = <span className="text-primary font-bold">{fmt(run)}"</span></p>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   PIPE FRICTION LOSS CALCULATOR
   ═══════════════════════════════════════════════════════ */
export const FrictionLossCalculator = () => {
  const defaults = readSettings();
  const [materialIdx, setMaterialIdx] = useState<string>(defaults.pipeMaterial);
  const [sizeIdx, setSizeIdx] = useState("0");
  const [flowGPM, setFlowGPM] = useState("");
  const [lengthFt, setLengthFt] = useState("");

  const mat = PIPE_MATERIALS_HW[parseInt(materialIdx)];
  const pipe = mat.ids[parseInt(sizeIdx)] ?? mat.ids[0];
  const Q = parseFloat(flowGPM);
  const L = parseFloat(lengthFt);
  const C = mat.c;
  const d = pipe.id;
  const hasInput = Q > 0 && L > 0;
  const lossPerHundred = hasInput ? (4.52 * Math.pow(Q, 1.85)) / (Math.pow(C, 1.85) * Math.pow(d, 4.87)) : 0;
  const totalLoss = lossPerHundred * (L / 100);
  const velocity = hasInput ? (0.4085 * Q) / (d * d) : 0;
  const headLoss = totalLoss * 2.31;

  return (
    <div className="px-4 space-y-4">
      <div className="bg-card border border-border rounded-xl p-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Calculate <span className="text-foreground font-semibold">friction loss</span> (pressure drop) in piping using the Hazen-Williams formula for water systems at 60°F.
        </p>
      </div>
      <div className="space-y-3">
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Pipe Material</label>
          <Select value={materialIdx} onValueChange={(v) => { setMaterialIdx(v); setSizeIdx("0"); }}>
            <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>{PIPE_MATERIALS_HW.map((m, i) => (<SelectItem key={i} value={String(i)}>{m.label} (C={m.c})</SelectItem>))}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Pipe Size</label>
          <Select value={sizeIdx} onValueChange={setSizeIdx}>
            <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>{mat.ids.map((s, i) => (<SelectItem key={i} value={String(i)}>{s.name} (ID {s.id}")</SelectItem>))}</SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Flow Rate (GPM)</label>
            <Input type="number" inputMode="decimal" placeholder="GPM…" value={flowGPM} onChange={(e) => setFlowGPM(e.target.value)} className="font-mono-data bg-card border-border" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Pipe Length (ft)</label>
            <Input type="number" inputMode="decimal" placeholder="Feet…" value={lengthFt} onChange={(e) => setLengthFt(e.target.value)} className="font-mono-data bg-card border-border" />
          </div>
        </div>
      </div>
      {hasInput && (
        <div className="space-y-2 animate-fade-in">
          <ResultCard label="Total Pressure Drop" value={fmt(totalLoss, 3)} unit="PSI" accent />
          <ResultCard label="Loss per 100 ft" value={fmt(lossPerHundred, 3)} unit="PSI/100ft" accent />
          <ResultCard label="Head Loss" value={fmt(headLoss, 2)} unit="ft head" />
          <ResultCard label="Flow Velocity" value={fmt(velocity, 2)} unit="ft/s" />
          {velocity > 8 && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-3">
              <p className="text-xs text-destructive font-semibold">⚠️ Velocity exceeds 8 ft/s — risk of erosion and water hammer.</p>
            </div>
          )}
          {velocity > 5 && velocity <= 8 && (
            <div className="bg-safety/10 border border-safety/30 rounded-xl p-3">
              <p className="text-xs text-safety font-semibold">⚡ Velocity above 5 ft/s — acceptable for short runs but may cause noise.</p>
            </div>
          )}
        </div>
      )}
      {hasInput && (
        <div className="bg-card border border-border rounded-xl p-4 mt-2 animate-fade-in">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Breakdown</h3>
          <div className="space-y-1.5 text-xs font-mono-data text-muted-foreground">
            <p>Hazen-Williams: ΔP/100ft = 4.52 × Q¹·⁸⁵ / (C¹·⁸⁵ × d⁴·⁸⁷)</p>
            <p className="text-foreground">= 4.52 × {Q}¹·⁸⁵ / ({C}¹·⁸⁵ × {d}⁴·⁸⁷)</p>
            <p className="text-foreground">= <span className="text-primary font-bold">{fmt(lossPerHundred, 3)} PSI/100ft</span></p>
            <p className="mt-2">Total = {fmt(lossPerHundred, 3)} × ({L} / 100) = <span className="text-primary font-bold">{fmt(totalLoss, 3)} PSI</span></p>
            <p className="mt-2">Velocity = 0.4085 × {Q} / {d}² = <span className="text-primary font-bold">{fmt(velocity, 2)} ft/s</span></p>
          </div>
        </div>
      )}
      <div className="mt-4">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Hazen-Williams C Values</h3>
        <div className="grid grid-cols-2 gap-2 text-center text-xs">
          {[
            { mat: "New Steel", c: 120 }, { mat: "Aged Steel", c: 100 },
            { mat: "Copper", c: 140 }, { mat: "PVC/CPVC", c: 150 },
            { mat: "Cast Iron (new)", c: 130 }, { mat: "Cast Iron (old)", c: 80 },
          ].map((r) => (
            <div key={r.mat} className="bg-card rounded-lg p-2 border border-border">
              <div className="text-muted-foreground">{r.mat}</div>
              <div className="font-mono-data font-bold text-primary mt-0.5">C = {r.c}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   EQUIVALENT LENGTH FITTINGS CALCULATOR
   ═══════════════════════════════════════════════════════ */
export const EquivalentLengthCalculator = () => {
  const defaults = readSettings();
  const [materialIdx, setMaterialIdx] = useState<string>(defaults.pipeMaterial);
  const [pipeSize, setPipeSize] = useState("1\"");
  const [flowGPM, setFlowGPM] = useState("");
  const [straightLength, setStraightLength] = useState("");
  const [fittingCounts, setFittingCounts] = useState<Record<string, number>>({});

  const mat = EQ_MATERIALS[parseInt(materialIdx)];
  const sizeData = EQ_LENGTH_DATA[pipeSize];
  const d = mat.ids[pipeSize];
  const Q = parseFloat(flowGPM);
  const L = parseFloat(straightLength) || 0;
  const C = mat.c;
  const availableSizes = EQ_PIPE_SIZES.filter((s) => s in mat.ids);

  const setCount = (fitting: string, val: string) => {
    const n = parseInt(val) || 0;
    setFittingCounts((prev) => ({ ...prev, [fitting]: n }));
  };

  const fittingDetails = FITTING_TYPES.map((f) => {
    const count = fittingCounts[f] || 0;
    const eqLen = sizeData?.[f] ?? 0;
    return { name: f, count, eqLen, total: count * eqLen };
  }).filter((f) => f.count > 0);

  const totalEqLength = fittingDetails.reduce((sum, f) => sum + f.total, 0);
  const totalEffectiveLength = L + totalEqLength;
  const hasInput = Q > 0 && totalEffectiveLength > 0 && d > 0;
  const lossPerHundred = hasInput ? (4.52 * Math.pow(Q, 1.85)) / (Math.pow(C, 1.85) * Math.pow(d, 4.87)) : 0;
  const straightLoss = lossPerHundred * (L / 100);
  const fittingLoss = lossPerHundred * (totalEqLength / 100);
  const totalLoss = lossPerHundred * (totalEffectiveLength / 100);
  const velocity = hasInput ? (0.4085 * Q) / (d * d) : 0;
  const fmtLocal = (n: number, dec = 2) => n.toFixed(dec);

  return (
    <div className="px-4 space-y-4">
      <div className="bg-card border border-border rounded-xl p-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Calculate <span className="text-foreground font-semibold">equivalent length</span> of fittings and total friction loss for a piping run.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Material</label>
          <Select value={materialIdx} onValueChange={(v) => { setMaterialIdx(v); const newMat = EQ_MATERIALS[parseInt(v)]; if (!(pipeSize in newMat.ids)) setPipeSize(Object.keys(newMat.ids)[0]); }}>
            <SelectTrigger className="bg-card border-border font-mono-data text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{EQ_MATERIALS.map((m, i) => (<SelectItem key={i} value={String(i)}>{m.label}</SelectItem>))}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Pipe Size</label>
          <Select value={pipeSize} onValueChange={setPipeSize}>
            <SelectTrigger className="bg-card border-border font-mono-data text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{availableSizes.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Flow (GPM)</label>
          <Input type="number" inputMode="decimal" placeholder="GPM…" value={flowGPM} onChange={(e) => setFlowGPM(e.target.value)} className="font-mono-data bg-card border-border" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Straight Pipe (ft)</label>
          <Input type="number" inputMode="decimal" placeholder="Feet…" value={straightLength} onChange={(e) => setStraightLength(e.target.value)} className="font-mono-data bg-card border-border" />
        </div>
      </div>
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Fittings Count</h3>
        <div className="grid grid-cols-2 gap-2">
          {FITTING_TYPES.map((f) => {
            const eqLen = sizeData?.[f] ?? 0;
            if (f === "Butterfly Valve" && eqLen === 0) return null;
            return (
              <div key={f} className="bg-card border border-border rounded-lg p-2 flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-foreground truncate">{f}</div>
                  <div className="text-[10px] text-muted-foreground font-mono-data">{eqLen} ft eq.</div>
                </div>
                <Input type="number" inputMode="numeric" min="0" value={fittingCounts[f] || ""} onChange={(e) => setCount(f, e.target.value)} placeholder="0" className="w-14 h-8 text-center font-mono-data bg-secondary border-border text-sm p-1" />
              </div>
            );
          })}
        </div>
      </div>
      {(fittingDetails.length > 0 || L > 0) && (
        <div className="space-y-2 animate-fade-in">
          {fittingDetails.length > 0 && (
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-2 text-muted-foreground font-semibold">Fitting</th>
                    <th className="text-center p-2 text-muted-foreground font-semibold">Qty</th>
                    <th className="text-center p-2 text-muted-foreground font-semibold">Eq. ft ea.</th>
                    <th className="text-right p-2 text-muted-foreground font-semibold">Total ft</th>
                  </tr>
                </thead>
                <tbody>
                  {fittingDetails.map((f) => (
                    <tr key={f.name} className="border-b border-border/50">
                      <td className="p-2 text-foreground">{f.name}</td>
                      <td className="p-2 text-center font-mono-data">{f.count}</td>
                      <td className="p-2 text-center font-mono-data text-muted-foreground">{f.eqLen}</td>
                      <td className="p-2 text-right font-mono-data text-primary font-bold">{fmtLocal(f.total, 1)}</td>
                    </tr>
                  ))}
                  <tr className="bg-secondary/30">
                    <td colSpan={3} className="p-2 font-semibold text-foreground">Total Equivalent Length</td>
                    <td className="p-2 text-right font-mono-data text-primary font-bold">{fmtLocal(totalEqLength, 1)} ft</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <ResultCard label="Straight Pipe" value={fmtLocal(L, 1)} unit="ft" />
            <ResultCard label="Eq. Length (fittings)" value={fmtLocal(totalEqLength, 1)} unit="ft" />
          </div>
          <ResultCard label="Total Effective Length" value={fmtLocal(totalEffectiveLength, 1)} unit="ft" accent />
          {hasInput && (
            <>
              <div className="border-t border-border pt-2 space-y-2">
                <ResultCard label="Straight Pipe Loss" value={fmtLocal(straightLoss, 3)} unit="PSI" />
                <ResultCard label="Fitting Loss" value={fmtLocal(fittingLoss, 3)} unit="PSI" accent />
                <ResultCard label="Total Pressure Drop" value={fmtLocal(totalLoss, 3)} unit="PSI" accent />
                <ResultCard label="Flow Velocity" value={fmtLocal(velocity, 2)} unit="ft/s" />
              </div>
              {velocity > 8 && (
                <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-3">
                  <p className="text-xs text-destructive font-semibold">⚠️ Velocity exceeds 8 ft/s — risk of erosion and water hammer.</p>
                </div>
              )}
              {totalLoss > 0 && (
                <div className="bg-card border border-border rounded-xl p-3">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Loss Distribution</h4>
                  <div className="w-full h-4 bg-secondary rounded-full overflow-hidden flex">
                    <div className="h-full bg-primary transition-all" style={{ width: `${(straightLoss / totalLoss) * 100}%` }} />
                    <div className="h-full bg-copper transition-all" style={{ width: `${(fittingLoss / totalLoss) * 100}%` }} />
                  </div>
                  <div className="flex justify-between mt-1 text-[10px] text-muted-foreground">
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-primary inline-block" /> Pipe {fmtLocal((straightLoss / totalLoss) * 100, 0)}%</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-copper inline-block" /> Fittings {fmtLocal((fittingLoss / totalLoss) * 100, 0)}%</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   GPM FLOW RATE SIZING RECOMMENDATION TOOL
   ═══════════════════════════════════════════════════════ */
export const GpmSizingCalculator = () => {
  const defaults = readSettings();
  const [materialIdx, setMaterialIdx] = useState<string>(defaults.pipeMaterial);
  const [flowGPM, setFlowGPM] = useState("");
  const [presetIdx, setPresetIdx] = useState<string>(defaults.pipeVelocityPreset);
  const [customMin, setCustomMin] = useState("");
  const [customMax, setCustomMax] = useState("");

  const mat = GPM_SIZING_MATERIALS[parseInt(materialIdx)];
  const Q = parseFloat(flowGPM);
  const preset = VELOCITY_PRESETS[parseInt(presetIdx)];
  const isCustom = preset.label === "Custom";
  const vMin = isCustom ? parseFloat(customMin) || 0 : preset.min;
  const vMax = isCustom ? parseFloat(customMax) || 0 : preset.max;
  const hasInput = Q > 0 && vMin > 0 && vMax > 0 && vMax >= vMin;
  const idealIdForMaxV = hasInput ? Math.sqrt((0.4085 * Q) / vMax) : 0;
  const idealIdForMinV = hasInput ? Math.sqrt((0.4085 * Q) / vMin) : 0;

  const results = useMemo(() => {
    if (!hasInput) return [];
    return mat.sizes.map((s) => {
      const vel = (0.4085 * Q) / (s.id * s.id);
      const C = mat.c;
      const d = s.id;
      const lossPerHundred = (4.52 * Math.pow(Q, 1.85)) / (Math.pow(C, 1.85) * Math.pow(d, 4.87));
      const inRange = vel >= vMin && vel <= vMax;
      const tooFast = vel > vMax;
      const tooSlow = vel < vMin;
      return { name: s.name, id: s.id, vel, lossPerHundred, inRange, tooFast, tooSlow };
    });
  }, [hasInput, mat, Q, vMin, vMax]);

  const recommended = results.filter((r) => r.inRange);

  return (
    <div className="px-4 space-y-4">
      <div className="bg-card border border-border rounded-xl p-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Enter a <span className="text-foreground font-semibold">flow rate (GPM)</span> and select a velocity range. The tool recommends optimal pipe sizes.
        </p>
      </div>
      <div className="space-y-3">
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Pipe Material</label>
          <Select value={materialIdx} onValueChange={setMaterialIdx}>
            <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>{GPM_SIZING_MATERIALS.map((m, i) => (<SelectItem key={i} value={String(i)}>{m.label}</SelectItem>))}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Flow Rate (GPM)</label>
          <Input type="number" inputMode="decimal" placeholder="e.g. 50" value={flowGPM} onChange={(e) => setFlowGPM(e.target.value)} className="font-mono-data bg-card border-border" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Velocity Range</label>
          <Select value={presetIdx} onValueChange={setPresetIdx}>
            <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>{VELOCITY_PRESETS.map((p, i) => (<SelectItem key={i} value={String(i)}>{p.label}</SelectItem>))}</SelectContent>
          </Select>
        </div>
        {isCustom && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Min Velocity (ft/s)</label>
              <Input type="number" inputMode="decimal" placeholder="Min…" value={customMin} onChange={(e) => setCustomMin(e.target.value)} className="font-mono-data bg-card border-border" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Max Velocity (ft/s)</label>
              <Input type="number" inputMode="decimal" placeholder="Max…" value={customMax} onChange={(e) => setCustomMax(e.target.value)} className="font-mono-data bg-card border-border" />
            </div>
          </div>
        )}
      </div>
      {hasInput && (
        <div className="space-y-2 animate-fade-in">
          <div className="bg-card border border-border rounded-xl p-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Ideal Inside Diameter Range</h3>
            <div className="flex items-center gap-2 font-mono-data text-sm">
              <span className="text-primary font-bold">{idealIdForMaxV.toFixed(3)}"</span>
              <span className="text-muted-foreground">to</span>
              <span className="text-primary font-bold">{idealIdForMinV.toFixed(3)}"</span>
            </div>
          </div>
          {recommended.length > 0 && (
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-3">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-primary mb-2">✅ Recommended Sizes</h3>
              <div className="space-y-2">
                {recommended.map((r) => (
                  <div key={r.name} className="flex justify-between items-center bg-card rounded-lg p-2 border border-border">
                    <div>
                      <span className="font-mono-data font-bold text-foreground">{r.name}</span>
                      <span className="text-xs text-muted-foreground ml-2">(ID {r.id}")</span>
                    </div>
                    <div className="text-right">
                      <div className="font-mono-data text-sm font-bold text-primary">{r.vel.toFixed(2)} ft/s</div>
                      <div className="text-[10px] text-muted-foreground">{r.lossPerHundred.toFixed(3)} PSI/100ft</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {recommended.length === 0 && (
            <div className="bg-safety/10 border border-safety/30 rounded-xl p-3">
              <p className="text-xs text-safety font-semibold">⚠️ No standard pipe size keeps velocity within {vMin}–{vMax} ft/s at {Q} GPM.</p>
            </div>
          )}
          <div className="bg-card border border-border rounded-xl p-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">All Sizes Comparison</h3>
            <div className="overflow-x-auto -mx-1">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr>
                    <th className="data-table-header">Size</th><th className="data-table-header">ID (in)</th>
                    <th className="data-table-header">Velocity</th><th className="data-table-header">ΔP/100ft</th>
                    <th className="data-table-header">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r, i) => (
                    <tr key={r.name} className={`${r.inRange ? "bg-primary/5" : i % 2 === 0 ? "bg-card" : "bg-secondary/30"}`}>
                      <td className="data-table-cell font-mono-data font-semibold">{r.name}</td>
                      <td className="data-table-cell font-mono-data">{r.id}</td>
                      <td className={`data-table-cell font-mono-data font-bold ${r.inRange ? "text-primary" : r.tooFast ? "text-destructive" : "text-muted-foreground"}`}>{r.vel.toFixed(2)} ft/s</td>
                      <td className="data-table-cell font-mono-data">{r.lossPerHundred.toFixed(3)}</td>
                      <td className="data-table-cell">
                        {r.inRange && <span className="text-primary font-semibold">✅ Good</span>}
                        {r.tooFast && <span className="text-destructive font-semibold">🔴 Too fast</span>}
                        {r.tooSlow && <span className="text-muted-foreground">🔵 Too slow</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="bg-card border border-border rounded-xl p-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Velocity Distribution</h3>
            <div className="space-y-1.5">
              {results.map((r) => {
                const maxVel = Math.max(...results.map((x) => x.vel), vMax);
                const pct = Math.min((r.vel / maxVel) * 100, 100);
                return (
                  <div key={r.name} className="flex items-center gap-2">
                    <span className="text-[10px] font-mono-data w-10 text-right text-muted-foreground">{r.name}</span>
                    <div className="flex-1 h-4 bg-secondary/50 rounded-full overflow-hidden relative">
                      <div className="absolute top-0 bottom-0 bg-primary/10 border-x border-primary/30" style={{ left: `${(vMin / maxVel) * 100}%`, width: `${((vMax - vMin) / maxVel) * 100}%` }} />
                      <div className={`h-full rounded-full transition-all ${r.inRange ? "bg-primary" : r.tooFast ? "bg-destructive" : "bg-muted-foreground/40"}`} style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-[10px] font-mono-data w-14 text-muted-foreground">{r.vel.toFixed(1)} ft/s</span>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-3 mt-2 text-[10px] text-muted-foreground">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-primary inline-block" /> In range</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-destructive inline-block" /> Too fast</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-muted-foreground/40 inline-block" /> Too slow</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   MAX GPM CAPACITY REFERENCE CHART
   ═══════════════════════════════════════════════════════ */
export const MaxGpmChart = () => {
  const [materialIdx, setMaterialIdx] = useState("0");
  const mat = MAX_GPM_MATERIALS[parseInt(materialIdx)];

  const rows = mat.sizes.map((s) => {
    const gpms = MAX_GPM_VELOCITIES.map((v) => {
      const gpm = (v * s.id * s.id) / 0.4085;
      return Math.round(gpm);
    });
    return { name: s.name, id: s.id, gpms };
  });

  return (
    <div className="px-4 space-y-4">
      <div className="bg-card border border-border rounded-xl p-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Maximum <span className="text-foreground font-semibold">GPM capacity</span> for each pipe size at common velocity limits.
        </p>
      </div>
      <div>
        <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Pipe Material</label>
        <Select value={materialIdx} onValueChange={setMaterialIdx}>
          <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
          <SelectContent>{MAX_GPM_MATERIALS.map((m, i) => (<SelectItem key={i} value={String(i)}>{m.label}</SelectItem>))}</SelectContent>
        </Select>
      </div>
      <div className="overflow-x-auto mx-0 rounded-lg border border-border">
        <table className="w-full text-left">
          <thead>
            <tr>
              <th className="data-table-header">Size</th>
              <th className="data-table-header">ID (in)</th>
              {MAX_GPM_VELOCITIES.map((v) => (
                <th key={v} className="data-table-header text-center">
                  <div>{v} ft/s</div>
                  <div className="text-[9px] font-normal text-muted-foreground">{v <= 5 ? "domestic" : v <= 8 ? "HVAC" : v <= 10 ? "process" : "fire"}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.name} className={i % 2 === 0 ? "bg-card" : "bg-secondary/30"}>
                <td className="data-table-cell font-mono-data font-semibold">{r.name}</td>
                <td className="data-table-cell font-mono-data text-muted-foreground">{r.id}</td>
                {r.gpms.map((gpm, j) => (
                  <td key={j} className="data-table-cell font-mono-data text-center">
                    <span className={MAX_GPM_VELOCITIES[j] <= 5 ? "text-primary" : MAX_GPM_VELOCITIES[j] <= 8 ? "text-foreground" : MAX_GPM_VELOCITIES[j] <= 10 ? "text-safety" : "text-destructive"}>
                      {gpm.toLocaleString()}
                    </span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap gap-3 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-primary inline-block" /> Domestic (≤5 ft/s)</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-foreground inline-block" /> HVAC (6–8 ft/s)</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-safety inline-block" /> Process (10 ft/s)</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-destructive inline-block" /> Fire (15 ft/s)</span>
      </div>
      <div className="bg-card border border-border rounded-xl p-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Velocity Guidelines</h3>
        <div className="grid grid-cols-2 gap-2 text-xs">
          {[
            { use: "Domestic Water", range: "3–5 ft/s" }, { use: "HVAC Hydronic", range: "4–8 ft/s" },
            { use: "Process Piping", range: "5–10 ft/s" }, { use: "Fire Protection", range: "≤15 ft/s" },
            { use: "Suction Lines", range: "2–4 ft/s" }, { use: "Erosion Limit", range: ">8 ft/s ⚠️" },
          ].map((g) => (
            <div key={g.use} className="bg-secondary/30 rounded-lg p-2 border border-border">
              <div className="text-muted-foreground">{g.use}</div>
              <div className="font-mono-data font-bold text-foreground mt-0.5">{g.range}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
